/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { Dialog } from "../../../dialogs/Dialog";
import { ReleaseDialogComponent } from "./ReleaseDialogComponent";
import { IEditor } from "../../../IEditor";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";
import { IViewInstance, ViewService } from "../../../view/ViewService";
import { BrowserLogger } from "../../../common/BrowserLogger";
import { dataEqual } from "@adapptio/lib-blueprint";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { IErrorGroup } from "../../../errors/ErrorsTypes";
import { ErrorTracker } from "@adapptio/lib-components";
import { IEditorApiReleaseListResponse, IScmInfo } from "@adapptio/lib-core";
import { ScmService } from "../../../scm/ScmService";
import { DeploymentService } from "../../../deployment/DeploymentService";

export interface ReleaseDialog_Params {
	message: string;
}

export interface ReleaseDialog_Result {
	success: boolean;
}

export enum RELEASE_DIALOG_ACTION {
	RELEASE = "release",
	SAVE_AND_RELEASE = "saveAndRelease"
}

export interface IReleaseDialogStatusInfo {
	saving: boolean;
	currentAction: RELEASE_DIALOG_ACTION | null;
}

export class ReleaseDialog extends Dialog<ReleaseDialog_Params, ReleaseDialog_Result> {
	/* Services */
	private logger: BrowserLogger;
	private toastSvc: ToastService;
	private viewSvc: ViewService;
	private deploymentSvc: DeploymentService;
	private scmSvc: ScmService;

	/* Data */
	private releaseListData: IEditorApiReleaseListResponse = { releases: [], nextTagName: "" };
	private scmInfoData: IScmInfo;

	/* Events */
	public onScmInfoChange = createEventEmitter<void>();
	public onReleaseListChange = createEventEmitter<void>();
	public onUnsavedChangesChange = createEventEmitter<void>();
	public onStatusInfoChange = createEventEmitter<void>();
	public onErrorsChanged = createEventEmitter<void>();

	/** DeployStatement */
	private currentAction: RELEASE_DIALOG_ACTION = null;

	constructor(dc: IEditor, id: string, params: ReleaseDialog_Params) {
		super(dc, id, params);

		this.viewSvc = dc.get("viewService");
		this.deploymentSvc = dc.get("deploymentService");
		this.scmSvc = dc.get("scmService");
		this.toastSvc = dc.get("toastService");

		this.logger = dc.get("logger");

		this.updateScmInfo();
		this.updateReleaseList();

		onEvent(this.viewSvc.onViewListChanged, this.handleViewListChange);
		onEvent(this.viewSvc.onSavingChange, this.handleSavingChange);
		onEvent(this.scmSvc.onScmInfoChange, this.handleScmInfoChange);
		onEvent(this.scmSvc.onReleaseListChange, this.handleReleaseListChange);
		onEvent(this.deploymentSvc.onErrorsChanged, this.handleErrorsChange);

		this.scmSvc.loadReleaseList();
		this.scmSvc.loadScmInfo();
	}

	public dispose(): void {
		super.dispose();

		offEvent(this.viewSvc.onViewListChanged, this.handleViewListChange);
		offEvent(this.viewSvc.onSavingChange, this.handleSavingChange);
		offEvent(this.scmSvc.onScmInfoChange, this.handleScmInfoChange);
		offEvent(this.scmSvc.onReleaseListChange, this.handleReleaseListChange);
		offEvent(this.deploymentSvc.onErrorsChanged, this.handleErrorsChange);

		removeAllEventListeners(this.onUnsavedChangesChange);
		removeAllEventListeners(this.onStatusInfoChange);
		removeAllEventListeners(this.onErrorsChanged);
		removeAllEventListeners(this.onReleaseListChange);
	}

	private async updateScmInfo() {
		const scmInfo = this.scmSvc.getScmInfo();

		const changed = !dataEqual(scmInfo, this.scmInfoData);
		this.scmInfoData = scmInfo;

		if (changed) {
			emitEvent(this.onScmInfoChange);
		}
	}

	private handleScmInfoChange = () => {
		this.updateScmInfo();
	}

	private async updateReleaseList() {
		const releaseList = this.scmSvc.getReleaseList();

		const changed = !dataEqual(releaseList, this.releaseListData);
		this.releaseListData = releaseList;

		if (changed) {
			emitEvent(this.onReleaseListChange);
		}
	}

	private handleReleaseListChange = () => {		
		this.updateReleaseList();
	}

	public getScmInfo(): IScmInfo {
		return this.scmSvc.getScmInfo();
	}

	public getReleaseList(): IEditorApiReleaseListResponse {
		return this.releaseListData;
	}

	private handleViewListChange = () => {
		emitEvent(this.onUnsavedChangesChange);
	};

	public appHasUnsavedChanges(): boolean {
		return this.viewSvc.hasModifiedViews();
	}

	public getComponent(): typeof ReleaseDialogComponent {
		return ReleaseDialogComponent;
	}

	private handleSavingChange = (): void => {
		this.emitStatusInfoChange();
	};

	private async saveAppChanges(): Promise<void> {
		try {
			await this.viewSvc.saveAllViews();
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				message: "Saving all views failed. Try to save each modified view separately.",
				details: String(err)
			});

			throw err;
		}

		return;
	}

	private async doRelease(tagName: string, releaseNotes: string): Promise<void> {
		try {
			await this.scmSvc.createRelease(tagName, releaseNotes);

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				message: `Release ${tagName} has been created successfully.`,
				timeoutMs: 3000
			});
		} catch (err) {
			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				message: "Release has failed. Please try again later",
				details: String(err)
			});

			throw err;
		}
	}

	public async release(tagName: string, releaseNotes: string): Promise<void> {
		if (this.currentAction) {
			this.logger.warn(`Action ${this.currentAction} is already in progress.`);
		}

		this.setCurrentAction(RELEASE_DIALOG_ACTION.RELEASE);

		try {
			await this.doRelease(tagName, releaseNotes);
			this.logger.info("Released");
		} catch (err) {
			this.logger.error("Error while releasing app", err);
			throw err;
		} finally {
			this.setCurrentAction(null);
		}
	}

	public async saveAndRelease(tagName: string, releaseNotes: string): Promise<void> {
		if (this.currentAction) {
			this.logger.warn(`Action ${this.currentAction} is already in progress.`);
		}

		this.setCurrentAction(RELEASE_DIALOG_ACTION.SAVE_AND_RELEASE);

		try {
			await this.saveAppChanges();
			await this.doRelease(tagName, releaseNotes);

			this.logger.info("Saved and released");
		} catch (err) {
			this.logger.error("Error while saving and releasing app", err);
			throw err;
		} finally {
			this.setCurrentAction(null);
			this.handleViewListChange();
		}

		return;
	}

	private setCurrentAction(action: RELEASE_DIALOG_ACTION | null) {
		this.currentAction = action;
		this.emitStatusInfoChange();
	}


	private emitStatusInfoChange(): void {
		emitEvent(this.onStatusInfoChange);
	}

	public getStatusInfo(): IReleaseDialogStatusInfo {
		return {
			saving: this.viewSvc.isSaveInProgress(),
			currentAction: this.currentAction
		};
	}

	private handleErrorsChange = () => {
		emitEvent(this.onErrorsChanged);
	};

	public getErrors(): IErrorGroup[] {
		return this.deploymentSvc.getErrorGroups();
	}

	public reloadErrorList = (): Promise<void> => {
		return this.deploymentSvc.loadErrors();
	};

	public getUnsavedViewsInstances(): IViewInstance[] {
		return this.viewSvc.getUnsavedViewsInstances();
	}
}
